import React, { useMemo } from 'react';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, User } from 'lucide-react';

const statusColors = {
  todo: { bg: '#1A2B5E', light: '#1A2B5E15' },
  in_progress: { bg: '#106EFD', light: '#106EFD20' },
  review: { bg: '#E171EA', light: '#E171EA20' },
  done: { bg: '#37C563', light: '#37C56320' },
};

const statusLabels = { todo: 'To Do', in_progress: 'In Progress', review: 'In Review', done: 'Complete' };
const priorityStyle = (p) => p === 'high' ? 'bg-red-100 text-red-700' : p === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700';

const DAY_MS = 86400000;

const GanttTimeline = ({ tasks = [], users = [], projects = [], groupByProject = false, onTaskClick }) => {
  const { timeRange, dayWidth, totalDays, monthHeaders, tasks: processedTasks } = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // Collect all dates from tasks
    const allDates = [];
    tasks.forEach(t => {
      if (t.start_date) allDates.push(new Date(t.start_date));
      if (t.due_date) allDates.push(new Date(t.due_date));
    });

    if (allDates.length === 0) {
      // Default: show current month
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
      allDates.push(start, end);
    }

    // Add some padding
    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())) - 7 * DAY_MS);
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())) + 14 * DAY_MS);
    minDate.setHours(0, 0, 0, 0);
    maxDate.setHours(0, 0, 0, 0);

    const totalDays = Math.max(Math.ceil((maxDate - minDate) / DAY_MS), 30);
    const dayWidth = Math.max(28, Math.min(48, 1200 / totalDays));

    // Month headers
    const months = [];
    let cursor = new Date(minDate);
    while (cursor < maxDate) {
      const monthStart = new Date(cursor);
      const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
      const end = monthEnd > maxDate ? maxDate : monthEnd;
      const days = Math.ceil((end - monthStart) / DAY_MS) + 1;
      months.push({
        label: monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        days,
        startOffset: Math.floor((monthStart - minDate) / DAY_MS),
      });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }

    // Process tasks with positioning
    const processed = tasks.map(t => {
      const start = t.start_date ? new Date(t.start_date) : (t.due_date ? new Date(new Date(t.due_date).getTime() - 7 * DAY_MS) : now);
      const end = t.due_date ? new Date(t.due_date) : (t.start_date ? new Date(new Date(t.start_date).getTime() + 7 * DAY_MS) : new Date(now.getTime() + 7 * DAY_MS));
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      const leftDays = Math.max(0, (start - minDate) / DAY_MS);
      const widthDays = Math.max(1, (end - start) / DAY_MS);

      return {
        ...t,
        _left: leftDays * dayWidth,
        _width: Math.max(widthDays * dayWidth, 60),
        _start: start,
        _end: end,
        _isOverdue: t.due_date && new Date(t.due_date) < now && t.status !== 'done',
      };
    });

    return {
      timeRange: { min: minDate, max: maxDate },
      dayWidth,
      totalDays,
      monthHeaders: months,
      tasks: processed,
    };
  }, [tasks]);

  // Group tasks by project if needed
  const groups = useMemo(() => {
    if (!groupByProject) return [{ id: 'all', name: null, tasks: processedTasks }];

    const projectMap = {};
    const standalone = [];

    processedTasks.forEach(t => {
      if (t.project_id) {
        if (!projectMap[t.project_id]) {
          const proj = projects.find(p => p.id === t.project_id);
          projectMap[t.project_id] = { id: t.project_id, name: proj?.title || 'Unknown Project', tasks: [] };
        }
        projectMap[t.project_id].tasks.push(t);
      } else {
        standalone.push(t);
      }
    });

    const result = Object.values(projectMap).sort((a, b) => a.name.localeCompare(b.name));
    if (standalone.length) result.push({ id: 'standalone', name: 'Standalone Tasks', tasks: standalone });
    return result;
  }, [processedTasks, projects, groupByProject]);

  // Today marker position
  const todayOffset = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return ((now - timeRange.min) / DAY_MS) * dayWidth;
  }, [timeRange, dayWidth]);

  const totalWidth = totalDays * dayWidth;

  if (tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-navy/40" data-testid="gantt-empty">
        <Calendar className="w-12 h-12 mb-3 opacity-50" />
        <p className="text-lg font-medium">No tasks with dates to display</p>
        <p className="text-sm mt-1">Add start or due dates to tasks to see them on the timeline</p>
      </div>
    );
  }

  return (
    <div className="border border-navy/10 rounded-xl bg-white overflow-hidden" data-testid="gantt-timeline">
      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-2.5 border-b border-navy/10 bg-gray-50/50">
        <span className="text-xs font-medium text-navy/50 mr-1">Status:</span>
        {Object.entries(statusLabels).map(([key, label]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: statusColors[key].bg }} />
            <span className="text-xs text-navy/60">{label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 ml-auto">
          <div className="w-0.5 h-4 bg-red-500 rounded" />
          <span className="text-xs text-navy/60">Today</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div style={{ minWidth: totalWidth + 220 }}>
          {/* Month headers */}
          <div className="flex border-b border-navy/10 sticky top-0 z-10 bg-white">
            <div className="w-[220px] min-w-[220px] px-4 py-2 text-xs font-semibold text-navy/50 border-r border-navy/10 bg-gray-50">
              Task
            </div>
            <div className="flex-1 flex relative">
              {monthHeaders.map((m, i) => (
                <div
                  key={i}
                  className="border-r border-navy/10 px-2 py-2 text-xs font-semibold text-navy/60 bg-gray-50"
                  style={{ width: m.days * dayWidth }}
                >
                  {m.label}
                </div>
              ))}
            </div>
          </div>

          {/* Groups and rows */}
          {groups.map(group => (
            <div key={group.id}>
              {group.name && (
                <div className="flex border-b border-navy/10 bg-[#1A2B5E]/[0.03]">
                  <div className="w-[220px] min-w-[220px] px-4 py-2 border-r border-navy/10">
                    <span className="text-sm font-semibold text-[#1A2B5E]">{group.name}</span>
                    <span className="text-xs text-navy/40 ml-2">({group.tasks.length})</span>
                  </div>
                  <div className="flex-1" />
                </div>
              )}

              {group.tasks.map(task => {
                const sc = statusColors[task.status] || statusColors.todo;
                const assignee = users.find(u => u.id === task.assignee_id);

                return (
                  <div
                    key={task.id}
                    className="flex border-b border-navy/5 hover:bg-blue-50/30 transition-colors group"
                    data-testid={`gantt-row-${task.id}`}
                  >
                    {/* Task name column */}
                    <div
                      className="w-[220px] min-w-[220px] px-4 py-2.5 border-r border-navy/10 flex items-center gap-2 cursor-pointer"
                      onClick={() => onTaskClick?.(task)}
                    >
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: sc.bg }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A2B5E] truncate">{task.title}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Badge className={`text-[9px] px-1.5 py-0 ${priorityStyle(task.priority)}`}>{task.priority}</Badge>
                          {assignee && (
                            <span className="text-[10px] text-navy/40 flex items-center gap-0.5">
                              <User className="w-2.5 h-2.5" />{assignee.name?.split(' ')[0]}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Timeline bar */}
                    <div className="flex-1 relative" style={{ height: 52 }}>
                      {/* Today line */}
                      <div
                        className="absolute top-0 bottom-0 w-px bg-red-400/40 z-[1]"
                        style={{ left: todayOffset }}
                      />

                      {/* Task bar */}
                      <div
                        className={`absolute top-2 rounded-md flex items-center px-2 text-xs font-medium text-white cursor-pointer
                          transition-all hover:shadow-md hover:brightness-110
                          ${task._isOverdue ? 'ring-2 ring-red-400 ring-offset-1' : ''}`}
                        style={{
                          left: task._left,
                          width: task._width,
                          height: 32,
                          backgroundColor: sc.bg,
                        }}
                        title={`${task.title}\n${task.start_date ? 'Start: ' + new Date(task.start_date).toLocaleDateString() : ''}\n${task.due_date ? 'Due: ' + new Date(task.due_date).toLocaleDateString() : ''}`}
                        onClick={() => onTaskClick?.(task)}
                        data-testid={`gantt-bar-${task.id}`}
                      >
                        <span className="truncate opacity-90 text-[11px]">
                          {task.title}
                        </span>
                        {task._isOverdue && (
                          <Clock className="w-3 h-3 ml-1 flex-shrink-0 text-red-200" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GanttTimeline;
